import React, { useState } from "react";
import PropTypes from "prop-types";
import { http, humanReadableBytes } from "react-invenio-forms";

import { i18next } from "@translations/invenio_rdm_records/i18next";

import { Button, Form, Icon, Label, Message, Modal } from "semantic-ui-react";

const REMOTE_TRANSFER_TYPE = "R";

// Status → colour/icon mapping for the verification indicator.
const VERIFY_UI = {
  accessible: { color: "green", icon: "check circle", label: "Accessible" },
  unverifiable: { color: "blue", icon: "question circle", label: "Could not verify" },
  error: { color: "red", icon: "times circle", label: "Not reachable" },
};

export const RemoteFileModal = ({ open, onClose, record, onSuccess }) => {
  const [url, setUrl] = useState("");
  const [filename, setFilename] = useState("");
  const [filenameAutoFilled, setFilenameAutoFilled] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Verification state
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null); // {status, size, message}
  const [unverifiableConfirmed, setUnverifiableConfirmed] = useState(false);

  const guessFilename = (value) => {
    try {
      const parsed = new URL(value);
      return decodeURIComponent(parsed.pathname.split("/").pop() || "");
    } catch {
      return "";
    }
  };

  const handleUrlChange = (_, { value }) => {
    setUrl(value);
    setVerifyResult(null);
    setUnverifiableConfirmed(false);
    if (!filename || filenameAutoFilled) {
      const guessed = guessFilename(value);
      setFilename(guessed);
      setFilenameAutoFilled(!!guessed);
    }
  };

  const reset = () => {
    setUrl("");
    setFilename("");
    setFilenameAutoFilled(false);
    setError("");
    setLoading(false);
    setVerifyResult(null);
    setUnverifiableConfirmed(false);
  };

  const closeModal = () => {
    reset();
    onClose();
  };

  const verifyUrl = async (targetUrl) => {
    if (!targetUrl) return null;
    setVerifying(true);
    setVerifyResult(null);
    try {
      // Submit task — returns 202 + task_id, or 400 on validation failure.
      const submitResp = await http.post("/api/remote-files/verify", {
        url: targetUrl,
      });
      const { task_id: taskId } = submitResp.data;
      if (!taskId) {
        // Immediate validation error returned as 200 (shouldn't happen but handle it).
        setVerifyResult(submitResp.data);
        return submitResp.data;
      }

      // Poll until done — 500ms interval, 30 attempts (15s max).
      for (let i = 0; i < 30; i++) {
        await new Promise((r) => setTimeout(r, 500));
        const pollResp = await http.get(`/api/remote-files/verify/poll/${taskId}`);
        if (pollResp.status === 200) {
          setVerifyResult(pollResp.data);
          return pollResp.data;
        }
        // 202 = still pending, keep polling.
      }

      const timeout = {
        status: "unverifiable",
        size: null,
        message: i18next.t(
          "Verification timed out — the server did not respond in time."
        ),
      };
      setVerifyResult(timeout);
      return timeout;
    } catch (e) {
      const result = e.response?.data || {
        status: "error",
        message: i18next.t("Verification failed"),
      };
      setVerifyResult(result);
      return result;
    } finally {
      setVerifying(false);
    }
  };

  const registerFile = async (
    recordPid,
    fileKey,
    fileUrl,
    initialSize,
    initialStatus
  ) => {
    try {
      await http.post("/api/remote-files/register", {
        record_pid: recordPid,
        file_key: fileKey,
        url: fileUrl,
        size: initialSize ?? null,
        initial_status: initialStatus ?? "unknown",
      });
    } catch (e) {
      // Non-blocking: registration failure should not prevent the upload.
      console.warn("Remote file registration failed:", e);
    }
  };

  const submit = async () => {
    setError("");

    if (!url || !filename) {
      setError(i18next.t("Remote file URL and filename are required."));
      return;
    }

    if (!record?.links?.files) {
      setError(i18next.t("Please save the draft before adding a remote file."));
      return;
    }

    // Run verification if not yet done.
    let result = verifyResult;
    if (!result) {
      result = await verifyUrl(url);
    }

    // Guard: verification returned null (network failure before any response).
    if (!result || !result.status) {
      setError(
        i18next.t(
          "Could not verify the URL. Please check your connection and try again."
        )
      );
      return;
    }

    // Block on hard errors.
    if (result.status === "error") {
      setError(result.message || i18next.t("The remote URL could not be reached."));
      return;
    }

    // For unverifiable: require explicit confirmation before proceeding.
    if (result?.status === "unverifiable" && !unverifiableConfirmed) {
      // The UI already shows the warning + confirm checkbox; do nothing here.
      return;
    }

    setLoading(true);

    try {
      await http.post(record.links.files, [
        {
          key: filename,
          transfer: { type: REMOTE_TRANSFER_TYPE, url },
        },
      ]);

      // Register for daily reachability monitoring, seeding the initial size/status
      // from the verification result so the dot is correct on first page load.
      // Parse PID from links.files ("/api/records/<pid>/draft/files") as the
      // authoritative source — record.id may not be populated yet on first save.
      const pid =
        record.id ||
        (() => {
          try {
            const parts = record.links?.files?.split("/") || [];
            const idx = parts.lastIndexOf("records");
            return idx !== -1 ? parts[idx + 1] : null;
          } catch {
            return null;
          }
        })();
      if (pid) {
        await registerFile(pid, filename, url, result?.size, result?.status);
      }

      setLoading(false);
      onSuccess();
    } catch (e) {
      const message =
        e.response?.data?.errors?.[0]?.messages?.[0] ||
        e.response?.data?.message ||
        i18next.t("Failed to add remote file.");
      setError(message);
      setLoading(false);
    }
  };

  const uiMeta = verifyResult ? VERIFY_UI[verifyResult.status] : null;
  const isBlocked = verifyResult?.status === "error";
  const needsConfirm =
    verifyResult?.status === "unverifiable" && !unverifiableConfirmed;

  return (
    <Modal size="small" open={open} onClose={closeModal}>
      <Modal.Header>{i18next.t("Add remote file")}</Modal.Header>

      <Modal.Content>
        {error && <Message negative content={error} />}

        <Form>
          <Form.Input
            required
            label={i18next.t("Remote file URL")}
            placeholder="https://example.org/file.txt"
            value={url}
            onChange={handleUrlChange}
            action={
              <Button
                type="button"
                basic
                loading={verifying}
                disabled={!url || verifying}
                onClick={() => verifyUrl(url)}
                title={i18next.t("Check if the URL is reachable")}
              >
                <Icon name="plug" />
                {i18next.t("Check")}
              </Button>
            }
          />

          {uiMeta && (
            <div style={{ marginTop: "-0.75rem", marginBottom: "1rem" }}>
              <Label color={uiMeta.color} basic>
                <Icon name={uiMeta.icon} />
                {i18next.t(uiMeta.label)}
                {verifyResult.size != null && (
                  <Label.Detail>{humanReadableBytes(verifyResult.size)}</Label.Detail>
                )}
              </Label>
              {verifyResult.size == null && verifyResult.status === "accessible" && (
                <span
                  style={{ marginLeft: "0.5rem", color: "#888", fontSize: "0.85em" }}
                >
                  {i18next.t("(size unknown)")}
                </span>
              )}
            </div>
          )}

          {needsConfirm && (
            <Message warning>
              <p>
                {verifyResult.message ||
                  i18next.t(
                    "The server did not respond to verification requests. The file may still be valid."
                  )}
              </p>
              <Form.Checkbox
                label={i18next.t(
                  "I understand the file could not be verified — add it anyway"
                )}
                checked={unverifiableConfirmed}
                onChange={(_, { checked }) => setUnverifiableConfirmed(checked)}
              />
            </Message>
          )}

          <Form.Input
            required
            label={i18next.t("Filename")}
            placeholder="file.txt"
            value={filename}
            onChange={(_, { value }) => {
              setFilename(value);
              setFilenameAutoFilled(false);
            }}
          />

          <Message info>
            {i18next.t(
              "The file will not be stored in the repository. Downloading it will redirect users to the external source."
            )}
          </Message>
        </Form>
      </Modal.Content>

      <Modal.Actions>
        <Button type="button" onClick={closeModal}>
          {i18next.t("Cancel")}
        </Button>

        <Button
          type="button"
          primary
          loading={loading || verifying}
          disabled={isBlocked || needsConfirm || loading || verifying}
          onClick={submit}
        >
          {i18next.t("Add file")}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

RemoteFileModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  record: PropTypes.object.isRequired,
  onSuccess: PropTypes.func,
};

RemoteFileModal.defaultProps = {
  onSuccess: () => window.location.reload(),
};
